import { ApolloClient } from '@apollo/client'
import { gql, TypedDocumentNode } from '@apollo/client'
import { BadRequestError, ResourceNotFoundError, UnauthorizedError } from '../../containers/errorTypes'
import { VehicleInfo } from './types'
import { useMutation } from '@apollo/client/react'
import { DocumentNode } from 'graphql'

interface RelatedTowingOrderResult {
  __typename: string
  towingOrder?: { id: number; status: string }
}

interface RelatedTowingRecordResult {
  __typename: string
  towingRecord?: { id: number; status: string; label: string; jobDetails: { towingDate: string }; vehicleDetails: { owner: string } }
}

export interface SearchVehicleRelatedTowingOrdersResponse { towingOrder: RelatedTowingOrderResult }
export interface SearchVehicleRelatedTowingRecordsResponse { towingRecord: RelatedTowingRecordResult }

export interface VehicleIdVariables { vehicleInfoId: number }
export interface SearchVehicleVariables { vehicleRegistrationNumber: string; refreshDataFromTrafi?: boolean }
export interface UpdateVehicleInfoRelationVariables { input: { towingRecordId?: number; towingOrderId?: number; vehicleInfoCheckId: number } }
export interface ResetTowingRecordVehicleInfoCheckVariables { input: { towingRecordId: number } }
export interface ResetTowingOrderVehicleInfoCheckVariables { input: { towingOrderId: number } }

export type VehicleInfoSearchResult = { __typename: string; vehicleInfo?: VehicleInfo }
export interface VehicleInfoSearchResponse { vehicleInfoSearch: VehicleInfoSearchResult }
export interface VehicleInfoGetResponse { vehicleInfo: VehicleInfoSearchResult }

export const SEARCH_VEHICLE_RELATED_TOWINGORDERS_QUERY: TypedDocumentNode<SearchVehicleRelatedTowingOrdersResponse, { id: number }> = gql`
  query towingOrder($id: Int!) {
    towingOrder(id: $id) {
      __typename
      ... on GetTowingOrderSuccess {
        towingOrder {
          id
          status
        }
      }
    }
  }
`

export const SEARCH_VEHICLE_RELATED_TOWINGRECORDS_QUERY: TypedDocumentNode<SearchVehicleRelatedTowingRecordsResponse, { id: number }> = gql`
  query towingRecord($id: Int!) {
    towingRecord(id: $id) {
      __typename
      ... on GetTowingRecordSuccess {
        towingRecord {
          id
          status
          label
          jobDetails {
            towingDate
          }
          vehicleDetails {
            owner
          }
        }
      }
    }
  }
`

export const vehicleInfoFragment = {
  success: gql`
    fragment VehicleInfoFields on VehicleInfo {
      id
      searchMeta {
        dataSource
        dataSourceLongText
        dataSourceShortText
        searchStatus
        trafiSearchPerformer
        trafiSearchDate
        daysSinceTrafiSearch
      }
      vehicle {
        serialNumber
        registrationNumber
        vehicleClass
        vehicleStatus
        brand
        model
        modelYear
        makeAndModel
        color
        fuel
        transmission
        driveTrain
        displacement
        length
        width
        height
        weight
        holderName
        holderAddress
        remarks
        maxNetEffect
        maxNetWeight
        maxTechNetWeight
        initializationDate
        lastInspectionDate
        lastInspectionResult
        lastInspectionResultDescription
        firstRegistrationDate
        nextInspectionDate
      }
      insurance {
        insuredParty
        insuranceCompany
        insuranceStartDate
        insuranceEndDate
      }
      ownersHolders {
        holdershipType
        ownershipType
        ownershipTypeDescription
        ownershipStartDate
        ownershipEndDate
        customer {
          id
          identifier
          name
          address
          zipcode
          city
          type
        }
      }
      restrictions {
        type
        description
        seriousnessLevel
        seriousnessDescription
      }
      transferPermit {
        purposeOfUse
        validityStarts
        validityEnds
        applicant {
            lastNameMainUnit
            firstNameSubUnit
            streetAddress
            postalCode
            city
        }
        route
      }
    }
  `,
}

export const SEARCH_VEHICLE_QUERY: TypedDocumentNode<VehicleInfoSearchResponse, SearchVehicleVariables> = gql`
  query vehicleInfoSearch($vehicleRegistrationNumber: String!, $refreshDataFromTrafi: Boolean) {
    vehicleInfoSearch(
      vehicleRegistrationNumber: $vehicleRegistrationNumber
      refreshDataFromTrafi: $refreshDataFromTrafi
    ) {
      __typename
      ... on VehicleInfoSuccess {
        vehicleInfo {
          ...VehicleInfoFields
        }
      }
      ... on BadRequestError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
  ${vehicleInfoFragment.success}
`

export const GET_VEHICLE_QUERY: TypedDocumentNode<VehicleInfoGetResponse, VehicleIdVariables> = gql`
  query vehicleInfo($vehicleInfoId: Int!) {
    vehicleInfo(vehicleInfoId: $vehicleInfoId) {
      __typename
      ... on VehicleInfoSuccess {
        vehicleInfo {
          ...VehicleInfoFields
        }
      }
      ... on BadRequestError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
  ${vehicleInfoFragment.success}
`

export interface VehicleInfoResponse {
  __typename: string
  vehicleInfo: VehicleInfo
}

export type VehicleInfoError = BadRequestError | UnauthorizedError

export const queryVehicleInfo = async (
  client: ApolloClient,
  vehicleRegistration: string,
  reFetch: boolean
): Promise<VehicleInfoSearchResult | undefined> => {
  try {

    const { data } = await client.query({
      query: SEARCH_VEHICLE_QUERY,
      variables: { vehicleRegistrationNumber: vehicleRegistration, refreshDataFromTrafi: reFetch },
      fetchPolicy: 'network-only',
    })

    return data?.vehicleInfoSearch

  } catch (e) {
    return undefined
  }
  
}

export const getVehicleInfo = async (client: ApolloClient, vehicleInfoId: number): Promise<VehicleInfoSearchResult | undefined> => {

  try {
    const { data } = await client.query({
      query: GET_VEHICLE_QUERY,
      variables: { vehicleInfoId },
      fetchPolicy: 'network-only',
  })

  return data?.vehicleInfo

  } catch(e) {
    return undefined
  }
 
}

export function isVehicleInfoError(x: any): x is VehicleInfoError{
  return !(x.__typename === 'VehicleInfoSuccess')
}

export const UPDATE_TOWING_RECORD_VEHICLE_INFO_RELATION: TypedDocumentNode<{ updateTowingRecordVehicleInfoRelation: VehicleInfoUpdateRelationResponse | VehicleUpdatedRelationError }, UpdateVehicleInfoRelationVariables> = gql`
  mutation UpdateTowingRecordVehicleInfoRelation($input: UpdateVehicleInfoCheckRelationInput!) {
    updateTowingRecordVehicleInfoRelation(input: $input)
    {
      __typename
      ... on UpdateVehicleInfoCheckRelationSuccess {
          lastModified
      }
      ... on  ResourceNotFoundError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
`


export type VehicleUpdatedRelationError = ResourceNotFoundError | UnauthorizedError

export interface VehicleInfoUpdateRelationResponse {
  __typename: string
  lastModified: string
  success: boolean 
}

export function isUpdateVehicleInfoRelationError(x: any): x is VehicleUpdatedRelationError{
  return !(x.__typename === 'UpdateVehicleInfoCheckRelationSuccess')
}

export const updateTowingRecordVehicleInfoRelation = async (
  client: ApolloClient,
  towingRecordId: number,
  vehicleInfoCheckId: number
): Promise<VehicleInfoUpdateRelationResponse | VehicleUpdatedRelationError> => {

  const input = {
     towingRecordId: towingRecordId, vehicleInfoCheckId: vehicleInfoCheckId
  }

  const { data } = await client.mutate({
    mutation: UPDATE_TOWING_RECORD_VEHICLE_INFO_RELATION,
    variables: { input },
  })
  const result = data!.updateTowingRecordVehicleInfoRelation

  if(isUpdateVehicleInfoRelationError(result)){
    return result
  }

  return result
}


export const UPDATE_TOWING_ORDER_VEHICLE_INFO_RELATION: TypedDocumentNode<{ updateTowingOrderVehicleInfoRelation: VehicleInfoUpdateRelationResponse | VehicleUpdatedRelationError }, UpdateVehicleInfoRelationVariables> = gql`
  mutation UpdateTowingOrderVehicleInfoRelation($input: UpdateVehicleInfoCheckRelationInput!) {
    updateTowingOrderVehicleInfoRelation(input: $input)
    {
      __typename
      ... on UpdateVehicleInfoCheckRelationSuccess {
          lastModified
      }
      ... on  ResourceNotFoundError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
`
export const updateTowingOrderVehicleInfoRelation = async (
  client: ApolloClient,
  towingOrderId: number,
  vehicleInfoCheckId: number
): Promise<VehicleInfoUpdateRelationResponse | VehicleUpdatedRelationError> => {

  const input = {
    towingOrderId: towingOrderId, vehicleInfoCheckId: vehicleInfoCheckId
  }

  const { data } = await client.mutate({
    mutation: UPDATE_TOWING_ORDER_VEHICLE_INFO_RELATION,
    variables: { input },
  })
  const result = data!.updateTowingOrderVehicleInfoRelation

  if(isUpdateVehicleInfoRelationError(result)){
    return result
  }

  return result as VehicleInfoUpdateRelationResponse
}



export const RESET_TOWING_RECORD_VEHICLE_INFO_CHECK: TypedDocumentNode<{ resetTowingRecordVehicleInfoCheck: ResetVehicleInfoCheckResponse | ResetVehicleInfoCheckError }, ResetTowingRecordVehicleInfoCheckVariables> = gql`
  mutation ResetTowingRecordVehicleInfoCheck($input: ResetTowingRecordVehicleInfoCheckInput!) {
    resetTowingRecordVehicleInfoCheck(input: $input)
    {
      __typename
      ... on ResetVehicleInfoCheckSuccess {
          lastModified
      }
      ... on  ResourceNotFoundError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
`


export type ResetVehicleInfoCheckError = ResourceNotFoundError | UnauthorizedError

export interface ResetVehicleInfoCheckResponse {
  __typename: string
  lastModified: string
  success: boolean 
}

export function isResetVehicleInfoCheckError(x: any): x is VehicleUpdatedRelationError{
  return !(x.__typename === 'ResetVehicleInfoCheckSuccess')
}

export const resetTowingRecordVehicleInfoCheck = async (client: ApolloClient, towingRecordId: number) => {

  const input = {
    towingRecordId: towingRecordId
  }
  
  const { data } = await client.mutate({
    mutation: RESET_TOWING_RECORD_VEHICLE_INFO_CHECK,
    variables: { input },
  })
  const result = data!.resetTowingRecordVehicleInfoCheck

  if(isResetVehicleInfoCheckError(result)) {
    return result
  }
  return result as ResetVehicleInfoCheckResponse
}



//rename success to ResetVehicleInfoCheckSuccess
export const RESET_TOWING_ORDER_VEHICLE_INFO_CHECK: TypedDocumentNode<{ resetTowingOrderVehicleInfoCheck: ResetVehicleInfoCheckResponse | ResetVehicleInfoCheckError }, ResetTowingOrderVehicleInfoCheckVariables> = gql`
  mutation ResetTowingOrderVehicleInfoCheck($input: ResetTowingOrderVehicleInfoCheckInput!) {
    resetTowingOrderVehicleInfoCheck(input: $input)
    {
      __typename
      ... on ResetVehicleInfoCheckSuccess {
          lastModified
      }
      ... on  ResourceNotFoundError {
        error {
          message
        }
      }
      ... on UnauthorizedError {
        error {
          message
        }
      }
    }
  }
`

export const resetTowingOrderVehicleInfoCheck = async (client: ApolloClient, towingOrderId: number) => {

  const input = {
    towingOrderId: towingOrderId
  }
  
  const { data } = await client.mutate({
    mutation: RESET_TOWING_ORDER_VEHICLE_INFO_CHECK,
    variables: { input },
  })
  const result = data!.resetTowingOrderVehicleInfoCheck

  if(isResetVehicleInfoCheckError(result)) {
    return result
  }
  return result as ResetVehicleInfoCheckResponse
}

export const useResetVehicleInfoCheck = (query: DocumentNode, options: useMutation.Options) => {
    return useMutation(query, options)
}